import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ACCESS_ASSIGNMENTS_KEY, fetchAssignments, revokeAssignment, SCOPE_TYPE_LABELS, type AccessAssignment,
} from "@/lib/accessControl";
import { AssignRoleDialog } from "./AssignRoleDialog";


export function AccessPeopleTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [removing, setRemoving] = useState<AccessAssignment | null>(null);

  const { data: assignments, isLoading, error } = useQuery({
    queryKey: ACCESS_ASSIGNMENTS_KEY,
    queryFn: fetchAssignments,
  });

  const revoke = useMutation({
    mutationFn: revokeAssignment,
    onSuccess: () => {
      toast.success("Role removed");
      qc.invalidateQueries({ queryKey: ["admin", "access"] });
      qc.invalidateQueries({ queryKey: ["all_user_roles"] });
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setRemoving(null),
  });

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return assignments ?? [];
    return (assignments ?? []).filter((a) =>
      [a.person?.full_name, a.person?.email, a.role_name, a.scope_label]
        .some((v) => v?.toLowerCase().includes(q)),
    );
  }, [assignments, filter]);

  const isExpired = (a: AccessAssignment) => !!a.expires_at && new Date(a.expires_at) <= new Date();

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between space-y-0">
        <div>
          <CardTitle>People with roles</CardTitle>
          <CardDescription>Everyone who holds a built-in or custom role, and where it applies.</CardDescription>
        </div>
        {canManage && (
          <Button onClick={() => setAssignOpen(true)} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" /> Assign role
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by name, email, role or scope"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-8"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : error ? (
          <p className="text-sm text-destructive">Could not load roles: {(error as Error).message}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No one matches.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Applies to</TableHead>
                  <TableHead>Ends</TableHead>
                  {canManage && <TableHead className="w-10" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={`${a.source}-${a.id}`} className={isExpired(a) ? "opacity-60" : undefined}>
                    <TableCell>
                      <div className="font-medium">{a.person?.full_name ?? "Deleted account"}</div>
                      <div className="text-xs text-muted-foreground">{a.person?.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {a.role_name}
                        {a.source === "system" && <Badge variant="outline">Built-in</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {a.scope_type
                        ? <span>{SCOPE_TYPE_LABELS[a.scope_type]}: {a.scope_label ?? "Removed"}</span>
                        : <span className="text-muted-foreground">Everywhere</span>}
                    </TableCell>
                    <TableCell>
                      {a.expires_at
                        ? <span>{format(new Date(a.expires_at), "MMM d, yyyy")}{isExpired(a) && " (ended)"}</span>
                        : <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${a.role_name} from ${a.person?.full_name ?? "this account"}`}
                          onClick={() => setRemoving(a)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <AssignRoleDialog open={assignOpen} onOpenChange={setAssignOpen} />

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove role?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing?.person?.full_name ?? "This account"} will lose the {removing?.role_name} role
              {removing?.scope_label ? ` for ${removing.scope_label}` : ""}. You can assign it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={revoke.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (removing) revoke.mutate(removing);
              }}
            >
              {revoke.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
